import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { JOINT_VENTURE_LINE } from "@sga/shared";
import { apiGet } from "../lib/api.js";

/**
 * The application footer.
 *
 * Deliberately not decoration. It carries the three things a coordinator otherwise has to ask
 * someone for:
 *
 *  - who owns this platform, which matters because the data in it belongs to a joint venture
 *    rather than to one company;
 *  - whether the system is actually healthy, from the same `/health/ready` probe the orchestrator
 *    uses — so "the export is stuck" and "Redis is down" stop being the same conversation;
 *  - the wall-clock time in Africa/Lagos. Every availability window in the platform is evaluated in
 *    Lagos time (CLAUDE.md, "Time"), and staff do not all sit in it. "Closes at 23:59" is only
 *    unambiguous next to the clock it closes by.
 */

interface Readiness {
  status: "ok" | "degraded";
  checks: Record<string, { state: "up" | "down"; latencyMs: number }>;
}

const lagosTime = (): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Lagos",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());

/** Ticks on the minute boundary rather than every 30s, so the displayed minute is never stale. */
const useLagosClock = (): string => {
  const [time, setTime] = useState(lagosTime);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (): void => {
      const msToNextMinute = 60_000 - (Date.now() % 60_000);
      timer = setTimeout(() => {
        setTime(lagosTime());
        schedule();
      }, msToNextMinute + 250);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  return time;
};

/**
 * A degraded probe answers 503, which the fetch wrapper turns into a rejection — so an error and a
 * `degraded` body mean the same thing here and are treated the same. No body is read on failure:
 * the dependency's own error string is for the server log, not for a coordinator's footer.
 */
const SystemStatus = (): JSX.Element => {
  const { data, isPending, isError } = useQuery({
    queryKey: ["health", "ready"],
    queryFn: () => apiGet<Readiness>("/health/ready"),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 30_000,
  });

  const healthy = !isError && data?.status === "ok";
  const label = isPending
    ? "Checking services"
    : healthy
      ? "All systems operational"
      : "Service degraded";

  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={healthy ? "Database and queue both responding" : undefined}
    >
      <span
        aria-hidden
        className={`h-1.5 w-1.5 rounded-full ${
          isPending ? "bg-muted-foreground/40" : healthy ? "bg-accent" : "bg-destructive"
        }`}
      />
      <span className={healthy || isPending ? undefined : "font-medium text-destructive"}>
        {label}
      </span>
    </span>
  );
};

export const AppFooter = (): JSX.Element => {
  const time = useLagosClock();

  return (
    <footer className="mt-auto border-t bg-card px-4 py-4 text-xs text-muted-foreground sm:px-6 sm:py-5">
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-foreground">
            Seplat Growth Academy
          </p>
          {/* The explanatory prose is desktop-only throughout this footer. On a phone it was
              taller than the page content it sat under. */}
          <p className="mt-0.5 hidden max-w-md sm:block">
            Scholarship, Teachers Empowerment and Loco Parentis — one platform for every programme,
            form and applicant, year over year.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 sm:flex-col sm:items-end">
          <SystemStatus />
          <span>
            <span className="tabular-nums text-foreground">{time}</span>
            <span className="hidden sm:inline">
              {" "}
              in Lagos — the timezone every form window is evaluated in
            </span>
            <span className="sm:hidden"> Lagos</span>
          </span>
          <a
            href="https://apply.seplatgrowthacademy.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-sm hover:text-foreground hover:underline"
          >
            Applicant site
            <ExternalLink aria-hidden className="h-3 w-3" />
          </a>
        </div>
      </div>

      <p className="mt-3 border-t pt-2.5 text-[11px] sm:mt-4 sm:pt-3">{JOINT_VENTURE_LINE}</p>
    </footer>
  );
};
